import React from "react";
import api from "@/lib/api";
import { CaretDown, MagnifyingGlass, Plus, ClockCounterClockwise, WarningCircle, CheckCircle, Paperclip, X, FilePdf, FileXls } from "@phosphor-icons/react";

/**
 * Per-agent gap-fill wizard. Renders structured fields (with Oracle-backed
 * dropdowns + recent-value chips) above the natural-language prompt. The agent
 * run is gated on every ``required`` field having a non-empty value.
 *
 * Props:
 *   agentKey       string         current ERP agent
 *   values         dict           controlled field_values map
 *   onChange       fn(newValues)  setter
 *   missingFields  string[]       names that the last "needs_input" pointed at
 *   onCreateNew    fn(agentKey)   launches the linked create_agent (e.g. supplier-create)
 */

const fmtSub = (r) => (r.sub ? ` · ${r.sub}` : "");

function LookupField({ field, value, onChange, missing, onCreateNew, lookupSpecs }) {
  const [q, setQ] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");
  const inputRef = React.useRef();
  const create_agent_key = lookupSpecs?.[field.lookup]?.create_agent;

  const fetchRows = React.useCallback(async (term) => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get(`/erp-agents/lookups/${field.lookup}`, {
        params: { q: term || "", limit: 15 },
      });
      setRows(data.rows || []);
      if (data.status !== "ok") {
        setErr(data.status === "auth_error"
          ? "Oracle auth failed — using cached values."
          : data.status === "unconfigured"
            ? "Set up your Oracle profile to enable live lookups."
            : data.source === "stale_cache" ? "Oracle unreachable — showing cached values."
            : "");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Lookup failed.");
      setRows([]);
    } finally { setLoading(false); }
  }, [field.lookup]);

  // Lazy-load when dropdown opens or query changes
  React.useEffect(() => {
    if (!open) return;
    const id = setTimeout(() => fetchRows(q), 200);
    return () => clearTimeout(id);
  }, [open, q, fetchRows]);

  // Close on outside click
  React.useEffect(() => {
    const onDoc = (e) => {
      if (!inputRef.current?.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (row) => {
    onChange(row.value);
    setQ("");
    setOpen(false);
  };

  const shownLabel = (() => {
    if (!value) return "";
    const match = rows.find((r) => r.value === value);
    return match ? `${match.label}${fmtSub(match)}` : value;
  })();

  return (
    <div ref={inputRef} className="relative" data-testid={`wizard-field-${field.name}`}>
      <div className={`flex items-center gap-2 border px-3 py-2 text-sm cursor-text ${
        missing ? "border-[#DC2626] bg-red-50" : "border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#0020C2]"
      }`} onClick={() => setOpen(true)}>
        <MagnifyingGlass size={14} weight="bold" className="text-gray-400 shrink-0" />
        <input
          data-testid={`wizard-input-${field.name}`}
          value={open ? q : shownLabel}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={field.placeholder || `Search ${field.label}…`}
          className="flex-1 outline-none bg-transparent font-mono text-[13px]"
        />
        {value && !open && (
          <CheckCircle size={14} weight="fill" className="text-emerald-500 shrink-0" />
        )}
        <CaretDown size={12} weight="bold" className="text-gray-400 shrink-0" />
      </div>

      {/* Recent chips (only when input is empty + has recents) */}
      {!open && !value && (field.recent || []).length > 0 && (
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap" data-testid={`wizard-recent-${field.name}`}>
          <ClockCounterClockwise size={10} weight="bold" className="text-gray-400" />
          {field.recent.map((r) => (
            <button key={r.value} onClick={() => onChange(r.value)}
              className="text-[10px] font-mono px-1.5 py-0.5 border border-gray-300 text-gray-700 hover:border-[#0020C2] hover:text-[#0020C2]">
              {r.label}
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 border border-gray-300 bg-white shadow-lg max-h-72 overflow-y-auto"
             data-testid={`wizard-dropdown-${field.name}`}>
          {loading && <div className="px-3 py-2 text-xs text-gray-500">Searching Oracle…</div>}
          {!loading && err && (
            <div className="px-3 py-2 text-xs border-b border-gray-100 bg-amber-50 text-amber-800 flex items-start gap-1.5">
              <WarningCircle size={12} weight="fill" className="mt-0.5 shrink-0" />
              <span>{err}</span>
            </div>
          )}
          {!loading && rows.length === 0 && !err && (
            <div className="px-3 py-3 text-xs text-gray-500">
              No matches for &quot;{q}&quot;.
              {create_agent_key && (
                <button onClick={() => onCreateNew?.(create_agent_key)}
                  data-testid={`wizard-create-new-${field.name}`}
                  className="ml-2 inline-flex items-center gap-1 font-bold text-[#0020C2] hover:underline">
                  <Plus size={11} weight="bold" /> Create new
                </button>
              )}
            </div>
          )}
          {!loading && rows.map((r) => (
            <button key={r.value}
              data-testid={`wizard-option-${field.name}-${r.value}`}
              onClick={() => pick(r)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-b-0">
              <div className="font-medium text-gray-900">{r.label}</div>
              {r.sub && <div className="text-[10px] font-mono text-gray-500">{r.sub}</div>}
            </button>
          ))}
          {create_agent_key && rows.length > 0 && (
            <button onClick={() => onCreateNew?.(create_agent_key)}
              data-testid={`wizard-create-new-${field.name}`}
              className="w-full text-left px-3 py-2 text-xs font-bold text-[#0020C2] hover:bg-blue-50 inline-flex items-center gap-1 border-t border-gray-200">
              <Plus size={11} weight="bold" /> Create new {field.label.toLowerCase()}…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FileField({ field, value, onChange }) {
  // ``value`` is a list of {attachment_id, file_name, size, content_type}
  const items = Array.isArray(value) ? value : [];
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const inputRef = React.useRef();

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true); setErr("");
    const next = [...items];
    for (const f of files) {
      const form = new FormData();
      form.append("file", f);
      try {
        const { data } = await api.post("/erp-agents/attachments/upload", form);
        next.push(data);
      } catch (e2) {
        setErr(e2?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    onChange(next);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async (idx) => {
    const it = items[idx];
    try { await api.delete(`/erp-agents/attachments/${it.attachment_id}`); } catch { /* ignore */ }
    onChange(items.filter((_, i) => i !== idx));
  };

  const iconFor = (name) => {
    const n = (name || "").toLowerCase();
    if (n.endsWith(".pdf")) return <FilePdf size={14} weight="fill" className="text-[#DC2626]" />;
    if (n.endsWith(".xlsx") || n.endsWith(".xls")) return <FileXls size={14} weight="fill" className="text-emerald-600" />;
    return <Paperclip size={14} className="text-gray-500" />;
  };

  return (
    <div data-testid={`wizard-file-${field.name}`}>
      <label className="border border-dashed border-gray-300 px-3 py-3 flex items-center gap-2 cursor-pointer hover:border-[#0020C2] hover:bg-blue-50/50 text-xs">
        <Paperclip size={14} weight="bold" className="text-[#0020C2]" />
        <span className="font-medium text-gray-700">{busy ? "Uploading…" : "Click to upload PDF / Excel files"}</span>
        <input ref={inputRef} type="file" multiple={field.multiple !== false}
          accept={field.accept || ".pdf,.xlsx,.xls"}
          onChange={onPick} disabled={busy}
          data-testid={`wizard-file-input-${field.name}`}
          className="hidden" />
      </label>
      {err && <div className="text-[11px] text-[#DC2626] mt-1">{err}</div>}
      {items.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`wizard-file-list-${field.name}`}>
          {items.map((it, i) => (
            <li key={it.attachment_id} className="flex items-center gap-2 border border-gray-200 px-2 py-1.5 text-xs bg-white">
              {iconFor(it.file_name)}
              <span className="flex-1 truncate font-mono">{it.file_name}</span>
              <span className="text-[10px] font-mono text-gray-400 shrink-0">{Math.ceil((it.size || 0) / 1024)} KB</span>
              <button onClick={() => remove(i)} data-testid={`wizard-file-remove-${field.name}-${i}`}
                className="text-gray-400 hover:text-[#DC2626]"><X size={12} weight="bold" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PlainField({ field, value, onChange, missing }) {
  const type = field.type === "number" ? "number" : (field.type === "date" ? "date" : "text");
  const inputClass = `w-full border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0020C2] ${
    missing ? "border-[#DC2626] bg-red-50" : "border-gray-300"
  }`;
  if (field.type === "textarea") {
    return (
      <textarea
        data-testid={`wizard-input-${field.name}`}
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        rows={4} placeholder={field.placeholder} className={inputClass}
      />
    );
  }
  return (
    <input type={type}
      data-testid={`wizard-input-${field.name}`}
      value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder} className={inputClass}
    />
  );
}

export default function WizardFields({ agentKey, values, onChange, missingFields, onCreateNew, fieldErrors = {}, fieldWarnings = {} }) {
  const [schema, setSchema] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSchema(null);
    api.get(`/erp-agents/agent-fields/${agentKey}`)
      .then(({ data }) => { if (!cancelled) setSchema(data); })
      .catch(() => { if (!cancelled) setSchema({ fields: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [agentKey]);

  // Pre-fill tenant defaults the first time the schema lands (only fields that are still empty)
  React.useEffect(() => {
    if (!schema?.fields) return;
    const seed = {};
    for (const f of schema.fields) {
      if (!values[f.name] && f.tenant_default) seed[f.name] = f.tenant_default;
    }
    if (Object.keys(seed).length) onChange({ ...values, ...seed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema]);

  if (loading) return <div className="text-xs text-gray-500" data-testid="wizard-loading">Loading wizard…</div>;
  if (!schema || !schema.fields || schema.fields.length === 0) return null;

  const missingSet = new Set(missingFields || []);
  const set = (name) => (v) => onChange({ ...values, [name]: v });

  return (
    <div className="border border-gray-200 bg-gradient-to-br from-blue-50/40 to-white p-4 space-y-3" data-testid="wizard-fields">
      <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2]">
        Guided wizard · {schema.fields.length} fields
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {schema.fields.map((f) => {
          const err = fieldErrors[f.name];
          const warn = fieldWarnings[f.name];
          const flagMissing = missingSet.has(f.name) || !!err;
          return (
            <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="text-[11px] font-bold uppercase tracking-wider text-gray-600 block mb-1">
                {f.label}
                {f.required && <span className="text-[#DC2626] ml-0.5">*</span>}
              </label>
              {f.type === "file"
                ? <FileField field={f} value={values[f.name] || []} onChange={set(f.name)} />
                : f.lookup
                  ? <LookupField field={f} value={values[f.name] || ""} onChange={set(f.name)}
                                  missing={flagMissing} onCreateNew={onCreateNew}
                                  lookupSpecs={schema.lookup_specs} />
                  : <PlainField  field={f} value={values[f.name] || ""} onChange={set(f.name)}
                                  missing={flagMissing} />}
              {err && (
                <div data-testid={`wizard-error-${f.name}`} className="text-[11px] text-[#DC2626] mt-1 flex items-start gap-1">
                  <WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              )}
              {!err && warn && (
                <div data-testid={`wizard-warning-${f.name}`} className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                  <WarningCircle size={11} weight="fill" className="mt-0.5 shrink-0" />
                  <span>{warn}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
